import { newUuid } from './utils/uuid';
/** Account-bound lifecycle. The database owns eligibility and the first terminal answer. */
export class TrialExitFeedbackController {
    context;
    snapshot = { pending: false, question: null };
    listeners = new Set();
    generation = 0;
    active = true;
    claimed = false;
    pendingRequests = new Set();
    constructor(context) {
        this.context = context;
    }
    getSnapshot = () => this.snapshot;
    subscribe = (listener) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
    update(snapshot) { this.snapshot = snapshot; for (const listener of this.listeners)
        listener(); }
    activate = () => { this.active = true; };
    dispose = () => {
        this.active = false;
        this.generation++;
        for (const controller of this.pendingRequests)
            controller.abort();
        this.pendingRequests.clear();
        this.update({ pending: false, question: null });
        // Accepted/attempted terminal writes retain their original identity and survive host close.
    };
    async attempt(run, abortable, timeout = 3000) {
        const controller = abortable ? new AbortController() : undefined;
        if (controller)
            this.pendingRequests.add(controller);
        let timer;
        try {
            return await Promise.race([run(controller?.signal), new Promise((_, reject) => {
                    timer = setTimeout(() => { controller?.abort(); reject(new Error('Feedback request timed out.')); }, timeout);
                })]);
        }
        finally {
            if (timer)
                clearTimeout(timer);
            if (controller)
                this.pendingRequests.delete(controller);
        }
    }
    offer = (offer) => {
        const { accountId, teamId, enabled, transport } = this.context;
        if (this.snapshot.pending || this.snapshot.question)
            return;
        if (!this.active || !accountId || !teamId || enabled === false || this.claimed) {
            offer.onFinish?.();
            return;
        }
        const generation = ++this.generation;
        const current = () => this.active && generation === this.generation;
        this.update({ pending: true, question: null });
        const request = { teamId, expectedUserId: accountId, requestId: newUuid(), closedScreen: offer.closedScreen };
        void (async () => {
            try {
                let result;
                try {
                    result = await this.attempt(signal => transport.claim(request, signal), true);
                }
                catch (error) {
                    if (!current())
                        return;
                    result = await this.attempt(signal => transport.claim(request, signal), true);
                }
                if (!current())
                    return;
                if (!result.eligible) {
                    this.update({ pending: false, question: null });
                    offer.onFinish?.();
                    return;
                }
                this.claimed = true;
                this.update({ pending: false, question: { ...offer, key: result.claimId, identity: { claimId: result.claimId, expectedUserId: accountId } } });
            }
            catch {
                if (current()) {
                    this.update({ pending: false, question: null });
                    offer.onFinish?.();
                }
            }
        })();
    };
    shown = (question) => {
        // Called by the rendered card only after it intersects a visible document.
        const transport = this.context.transport;
        void (async () => {
            try {
                await this.attempt(signal => transport.shown(question.identity, signal), false);
            }
            catch {
                try {
                    await this.attempt(signal => transport.shown(question.identity, signal), false);
                }
                catch { /* Server outbox retries accepted acknowledgements. */ }
            }
        })();
    };
    finish(question) {
        if (!this.active || this.snapshot.question !== question)
            return;
        this.update({ pending: false, question: null });
        question.onFinish?.();
    }
    send = async (question, answer) => {
        const request = { ...question.identity, outcome: 'submitted', reason: answer.reason, answer: answer.answer?.trim() || null };
        await this.attempt(() => this.context.transport.respond(request), false, 8000);
        this.finish(question);
    };
    skip = () => {
        const question = this.snapshot.question;
        if (!question)
            return;
        const request = { ...question.identity, outcome: 'skipped', reason: null, answer: null };
        const transport = this.context.transport;
        // Start before onFinish can destroy a native sidepanel. Never send unsubmitted text.
        void (async () => {
            try {
                await this.attempt(() => transport.respond(request), false);
            }
            catch {
                try {
                    await this.attempt(() => transport.respond(request), false);
                }
                catch { /* Closing remains optional even offline. */ }
            }
        })();
        this.finish(question);
    };
}
//# sourceMappingURL=trialExitFeedbackController.js.map